import { useMemoizedFn } from 'ahooks';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Dropdown, Space, Table } from 'tdesign-react';
import { connect } from 'react-redux';
import { orderBy } from 'lodash';
import { getTopicPartitionChartData } from '@/services/kopConsumerGroupService';
import useTable from '@/hooks/useTable';
import SvgIcon from '@/components/SvgIcon';
import { renderNumRate } from '@/utils/common';
import Dashboard from '../Dashboard';
import ResetOffsetModal from '../ResetOffsetModal';

function InnerTable({ detail, perms }) {
  const [visible, setVisible] = useState(false);
  const [currentRow, setCurrentRow] = useState(null);
  const [resetVisible, setResetVisible] = useState(false);

  const options = [
    { content: '重置Offset', value: 'resetOffset', resource: 'kop:consumer-group:reset-offset' },
  ].filter((item) => perms.includes(item.resource));

  const clickHandler = (value, row, e) => {
    e.stopPropagation();
    setCurrentRow(row);
    switch (value) {
      case 'resetOffset':
        setResetVisible(true);
        break;
      default:
        break;
    }
  };

  const columns = [
    {
      title: 'Partition',
      colKey: 'partition',
      width: 100,
      fixed: 'left',
    },
    {
      title: 'Member ID',
      colKey: 'memberId',
      width: 200,
    },
    {
      title: 'Status',
      colKey: 'status',
      width: 80,
    },
    {
      title: 'Current Offset',
      colKey: 'currentLogOffset',
      width: 120,
    },
    {
      title: 'Log End Offset',
      colKey: 'endLogOffset',
      width: 130,
    },
    {
      title: 'Lag',
      colKey: 'lag',
      width: 60,
    },
    {
      title: '消费速率',
      colKey: 'bytesOut',
      cell: renderNumRate,
      width: 80,
    },
    {
      title: 'Host',
      colKey: 'host',
      width: 170,
    },
    {
      title: 'Client ID',
      colKey: 'clientId',
      width: 120,
    },
    {
      colKey: 'option',
      title: '操作',
      fixed: 'right',
      align: 'center',
      cell: ({ row }) => (
        <Space size='small'>
          <Icon
            type='presentation-chart-line'
            onClick={() => {
              setVisible(true);
              setCurrentRow(row);
            }}
          />
          <Dropdown options={options} onClick={({ value }, e) => clickHandler(value, row, e)}>
            <span>
              <Icon type='dots-vertical' />
            </span>
          </Dropdown>
        </Space>
      ),
      width: 80,
    },
  ];

  const loadData = useMemoizedFn(() => {
    const tableData = orderBy(detail.kopTopicPartitionSubscriptionItem, ['partition'], ['asc']);
    setData(tableData);
  });

  const { config, setData, doSearch, doRefresh } = useTable({
    columns,
    onSearch: loadData,
  });

  useEffect(() => {
    doSearch();
  }, [doSearch]);

  const getTopicChartData = (params) => {
    return getTopicPartitionChartData({
      ...params,
      clusterName: detail.clusterName,
      consumerGroup: detail.consumerGroupName,
      topicName: detail.consumedTopicName,
      partition: currentRow.partition,
    });
  };

  const handleClose = (refresh) => {
    setVisible(false);
    setCurrentRow(null);
    setResetVisible(false);
    if (refresh) {
      doRefresh();
    }
  };

  return (
    <Root>
      <Table {...config} pagination={false} rowKey='partition' />
      <Dashboard
        clusterName={detail.clusterName}
        visible={visible}
        detail={{ ...detail, ...currentRow }}
        getChartsData={(params) =>
          getTopicChartData({
            clusterName: detail.clusterName,
            topicName: currentRow.topic,
            ...params,
          })
        }
        handleClose={handleClose}
      />
      <ResetOffsetModal
        visible={resetVisible}
        detail={{
          ...currentRow,
          clusterName: detail.clusterName,
          consumedTopicName: currentRow?.topic,
          consumerGroupName: detail.consumerGroupName,
        }}
        doClose={handleClose}
      />
    </Root>
  );
}

export default connect((state) => ({
  perms: state.auth.perms,
}))(InnerTable);

const Root = styled.div`
  background: rgb(247, 248, 250);
  .t-table,
  .t-table__header--fixed,
  .t-table tr,
  .t-table__header--fixed > tr > th {
    background-color: rgb(247, 248, 250);
  }
`;

const Icon = styled(SvgIcon)`
  cursor: pointer;
  color: #194dff;
`;
